import { Injectable } from '@angular/core'
import { Event, NavigationStart, Router } from '@angular/router'

import { Actions, Effect, ofType } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'

import { Observable, of } from 'rxjs'
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators'
import { toPayload, withPredicateFrom } from '../../core/helpers/rx-pipes'

import { ProductService } from '../services/product-service'

import * as product from '../actions/product'
import { GetFavoritesSuccess } from '../actions/product'
import * as facet from '../actions/facet'

import * as fromCatalog from '../reducers'
import * as fromBase from '../../base/reducers'
import * as fromAuth from '../../auth/reducers'
import { Product } from '../../core/models/product';
import chaining from '../../core/helpers/chaining';

@Injectable()
export class ProductEffects {
    constructor(
        private router: Router,
        private actions$: Actions,
        private productSvc: ProductService,
        private store: Store<fromCatalog.State | fromAuth.State>
    ) { }



    /**
     * Product Load Worker Effects
     */

    @Effect()
    loadWhenInStockToggled$: Observable<Action> =
        this.actions$.ofType<product.ToggleInStock>(product.TOGGLE_INSTOCK)
            .pipe(
              switchMap(() => [new product.Filter(), new facet.Filter()])
            )

    @Effect()
    filterProductsOnSetProductFilter$: Observable<Action> =
      this.actions$.ofType<product.SetProductFilter>(product.SET_PRODUCT_FILTER)
        .pipe(
          switchMap(() => [new product.Filter(), new facet.Filter()])
        )

    @Effect()
    setDefaultCatalogSortOrder$: Observable<Action> = this.actions$.pipe(
      ofType<product.SetProductDefaultSortOrder>(product.SET_PRODUCT_DEFAULT_SORT_ORDER),
      withLatestFrom(this.store.pipe(select(fromBase.getTenantSettings)), (action, tenantSettings) => ({ action, tenantSettings })),
      filter((data) => {
        const catalogSortOrders = data.tenantSettings['catalog_sort_orders']
        return (catalogSortOrders && catalogSortOrders.length > 0)
      }),
      map((data) => {
        const defaultSortOrder = data.tenantSettings['catalog_sort_orders'][0].key
        return new product.SetProductDefaultSortOrderSuccess(defaultSortOrder)
      })
    )

    @Effect()
    sortProductsOnSetProductSortOrder$: Observable<Action> =
      this.actions$.ofType<product.SetProductSortOrder>(product.SET_PRODUCT_SORT_ORDER)
        .pipe(
          switchMap(() => [new product.Filter()])
        )

    @Effect()
    filterProducts$: Observable<Action> =
        this.actions$.ofType<product.Filter>(product.FILTER)
        .pipe(
            withLatestFrom(
                this.store.pipe(select(fromCatalog.getProductFilterables)),
                this.store.pipe(select(fromBase.getPageParams)),
                this.store.pipe(select(fromCatalog.getProductSortOrder)),
                (action, filterables, params, sortOrder) => ({filterables, params, sortOrder })
            ),
            switchMap(data => {
              return this.productSvc
                    .findProducts(
                      data.filterables.customerId,
                      data.filterables.pageId,
                      data.filterables.facets,
                      data.filterables.onlyInStock,
                      null,
                      data.params,
                      data.filterables.searchTerm,
                      null,
                      null,
                      data.filterables.filterItems,
                      data.sortOrder)
                    .pipe(
                        map(products => new product.FilterSuccess(products)),
                        catchError(e => of(new product.FilterFailed(e)))
                    )
            })
        )

    /**
     * Product Expansion Effect
     */
    flushDistinctExpand$: Observable<Event> =
        this.router.events
            .pipe(
                filter((ev: Event) => ev instanceof NavigationStart
                    && ev.url.indexOf('/catalog') !== -1)
            )

    @Effect()
    expand$: Observable<Action> =
        this.actions$.ofType<product.Expand>(product.EXPAND)
            .pipe(
                withPredicateFrom(
                    this.store.pipe(select(fromCatalog.canExpand)),
                    canExpand => canExpand
                ),
                withLatestFrom(
                    this.store.pipe(select(fromCatalog.getCombinedExpandState)),
                    this.store.pipe(select(fromCatalog.getNextPageNumber)),
                    this.store.pipe(select(fromBase.getPageParams)),
                    this.store.pipe(select(fromCatalog.getProductSortOrder)),
                    (action, state, pageNumber, params, sortOrder) => ({state, pageNumber, params, sortOrder})
                ),
                // distinct(combined => combined.state.valueKey, this.flushDistinctExpand$),
                switchMap(combined => {
                  return this.productSvc
                        .findProducts(
                          combined.state.customerId,
                          combined.state.pageId,
                          combined.state.facets,
                          combined.state.onlyInStock,
                          combined.pageNumber.toString(),
                          combined.params,
                          combined.state.searchTerm,
                          null,
                          null,
                          combined.state.filterItems,
                          combined.sortOrder
                        )
                        .pipe(
                            map(products => new product.ExpandSuccess(products)),
                            catchError(e => of(new product.ExpandFailed(e)))
                        )
                })
            )

    /**
     * Product Add Effect
     */
    @Effect()
    addProduct$: Observable<Action> =
        this.actions$.ofType<product.Add>(product.ADD)
            .pipe(
                toPayload<string>(),
                withLatestFrom(
                  this.store.pipe(select(fromAuth.getCustomerId)),
                  (productId, customerId) => ({productId, customerId})
                ),
                switchMap(data => {
                    return this.productSvc.findProduct(data.productId, data.customerId)
                        .pipe(
                            switchMap(result => [new product.AddSuccess(result), new product.Select(result)]),
                            catchError(e => of(new product.AddFailed(e)))
                        )
                })
            )

    /**
     * Product Selection Effects
     */

    @Effect()
    addWhenNotLoaded$: Observable<Action> =
        this.actions$.ofType<product.Select>(product.SELECT)
          .pipe(
              toPayload<string | Product>(),
              withLatestFrom(this.store.pipe(select(fromCatalog.getProductIds))),
              filter(([item, ids]) => {
                if (typeof item === 'string')
                  return ids.indexOf(item) === -1
                else
                  return ids.indexOf(item && item._id) === -1
              }),
              map(([item, ids]) => {
                if (typeof item === 'string')
                  return new product.Add(item)
                else
                  return new product.Add(item && item._id)
              })
          )

    /**
     * Favorites effects
     */

    @Effect()
    getFavoritesIds$: Observable<Action> =
      this.actions$.ofType<product.GetFavorites>(product.GET_FAVORITES)
        .pipe(
          withLatestFrom(
            this.store.pipe(select(fromAuth.getCustomerId)),
            (action, customerId) => customerId
          ),
          switchMap(customerId => {
            return this.productSvc.getFavoritesIds(customerId)
              .pipe(
                map(favorites => new GetFavoritesSuccess(favorites)),
                catchError(e => of(new product.GetFavoritesFailed(e)))
              )
          })
        )

    @Effect()
    addFavorite$: Observable<Action> =
      this.actions$.ofType<product.AddFavorite>(product.ADD_FAVORITE)
        .pipe(
          toPayload<Product>(),
          withLatestFrom(
            this.store.pipe(select(fromAuth.getCustomerId)),
            (product, customerId) => ({product, customerId})
          ),
          switchMap(data => {
            return this.productSvc.addFavorite(data.customerId, data.product.code)
              .pipe(
                map(orderItem => new product.AddFavoriteSuccess(orderItem)),
                catchError(error => of(new product.AddFavoriteFailed(error)))
              )
          })
        )

    @Effect()
    removeFavorite$: Observable<Action> =
      this.actions$.ofType<product.RemoveFavorite>(product.REMOVE_FAVORITE)
        .pipe(
          toPayload<Product>(),
          withLatestFrom(
            this.store.pipe(select(fromCatalog.getFavorites)),
            this.store.pipe(select(fromBase.getPageParams)),
            (product, favorites, params) => {
              return {
                product,
                params,
                favoriteItem: favorites.find(item => item.productCode === product.code)
              }
            }
          ),
          switchMap(data => {
            return this.productSvc.removeFavorite(data.favoriteItem._id)
              .pipe(
                switchMap(res =>  {
                  if (data.params) {
                    return [
                      new product.RemoveFavoriteSuccess(data.product.code),
                      new product.Remove(data.product._id),
                      new facet.Filter()
                    ]
                  } else {
                    return [
                      new product.RemoveFavoriteSuccess(data.product.code)
                    ]
                  }
                }),
                catchError(error => of(new product.RemoveFavoriteFailed(error)))
              )
          })
        )

    @Effect()
    getProductOrderHistory$: Observable<Action> =
      this.actions$
        .pipe(
          ofType<product.Select>(product.SELECT),
          toPayload<Product>(),
          withPredicateFrom(
            this.store.pipe(select(fromAuth.getCustomerId)),
            customerId => !!customerId
          ),
          withLatestFrom(
            this.store.pipe(select(fromAuth.getCustomerId)),
            (product, customerId) => ({product, customerId})
          ),
          switchMap(data => {
            return this.productSvc.getProductOrderHistory(data.customerId, data.product && data.product.code)
              .pipe(
                map(response => new product.GetProductHistorySuccess(chaining(response, 'results'))),
                catchError(error => of(new product.GetProductHistoryFailed(error)))
              )
          })
        )
}
